import { Button, Checkbox, Form, Modal } from "antd";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { getDataTableColumns } from "~components/DataTable/utils/getDataTableColumns.ts";
import { ModalDivider } from "~components/ModalDivider/ModalDivider.tsx";
import { formStyles } from "~pages/Fixtures/FixtureManagePage/FixtureManagePage.styles.ts";
import type { DataTableColumnGroupDefinition } from "~types/dataTableColumnGroupDefinition.ts";
import { saveToLocalStorage } from "~utilities/saveToLocalStorage.ts";
import { sortStringValues } from "~utils/utilities.ts";

type Props = {
  columns: Array<{ key: string; title: string }>;
  tableConfiguration: Array<DataTableColumnGroupDefinition>;
  localStorageKey: string;
  setSelectedColumns: (fields: Array<string>) => void;
  modalWidth?: number;
};

export const DataTableColumnChooser = ({ columns, tableConfiguration, localStorageKey, setSelectedColumns, modalWidth }: Props) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [isSelectAllChecked, setSelectAllChecked] = useState(false);
  const { formatMessage } = useIntl();

  const columnTitles = useMemo(() => Object.fromEntries(columns.map((column) => [JSON.stringify(column.key), column.title])), [columns]);

  const handleOk = async () => {
    const { fields } = await form.validateFields();
    saveToLocalStorage(localStorageKey, JSON.stringify(fields));
    setSelectedColumns(fields);
    setOpen(false);
  };

  const allColumns = tableConfiguration.flatMap((x) => x.columns);

  const defaultColumns = allColumns.filter((x) => x.defaultInColumnChooser);

  const allColumnsStringifies = allColumns.map((x) => JSON.stringify(x.key));

  const defaultColumnsStringifies = defaultColumns.map((x) => JSON.stringify(x.key));

  return (
    <>
      <Modal
        width={modalWidth || 800}
        open={open}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
        }}
        afterOpenChange={(visible) => {
          if (visible) {
            form.setFieldsValue({ fields: getDataTableColumns(localStorageKey, tableConfiguration) });
          }
        }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <div style={{ display: "flex", gap: 5, justifyContent: "end" }}>
            <Button
              onClick={() => {
                setSelectAllChecked(false);
                form.setFieldsValue({
                  fields: defaultColumnsStringifies,
                });
              }}
              style={{ marginRight: "auto" }}
            >
              {formatMessage({ id: "default" })}
            </Button>
            <CancelBtn />
            <OkBtn />
          </div>
        )}
        onOk={handleOk}
        okText={formatMessage({ id: "ok" })}
      >
        <h5>
          <FormattedMessage id="table.settings.columns.modal.title" />
        </h5>
        <Form
          form={form}
          style={formStyles}
          onValuesChange={() => {
            const data = form.getFieldsValue(true);
            const formFieldsValues: Array<string> = data?.fields || [];
            const allFieldsSelected = formFieldsValues.length === allColumns.length;

            setSelectAllChecked(allFieldsSelected);
          }}
        >
          <Form.Item style={{ marginBottom: 0 }}>
            <Checkbox
              checked={isSelectAllChecked}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setSelectAllChecked(isChecked);
                const values = isChecked ? allColumnsStringifies : [];
                form.setFieldsValue({ fields: values });
              }}
            >
              <FormattedMessage id="select.all" />
            </Checkbox>
          </Form.Item>
          <Form.Item name="fields" rules={[{ required: true, message: formatMessage({ id: "table.settings.columns.error" }) }]}>
            <Checkbox.Group style={{ width: "100%" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", width: "100%" }}>
                {tableConfiguration
                  .sort((a, b) =>
                    sortStringValues(
                      formatMessage({ id: a.columnChooserTitleIntlKey }),
                      formatMessage({ id: b.columnChooserTitleIntlKey }),
                    ),
                  )
                  .map((group) => (
                    <div
                      key={`column-group-${group.columnChooserTitleIntlKey}`}
                      style={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%" }}
                    >
                      <ModalDivider translationKey={group.columnChooserTitleIntlKey} />
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {group.columns
                          .slice()
                          .sort((columnA, columnB) => {
                            const columnAKey = JSON.stringify(columnA.key);
                            const columnBKey = JSON.stringify(columnB.key);
                            return (columnTitles[columnAKey] || "").localeCompare(columnTitles[columnBKey] || "");
                          })
                          .map((columnDefinition) => {
                            const columnDefinitionDataIndexJson = JSON.stringify(columnDefinition.key);
                            const title = columnTitles[columnDefinitionDataIndexJson];

                            if (!title) {
                              return null;
                            }

                            return (
                              <div
                                key={columnDefinitionDataIndexJson}
                                style={{
                                  flex: "0 0 24%",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <Checkbox value={columnDefinitionDataIndexJson}>{title}</Checkbox>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ))}
              </div>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
      <Button onClick={() => setOpen(true)} type="text" className="table-header-button">
        <i className="fa-solid fa-columns"></i>
        <FormattedMessage id="table.column.settings" />
      </Button>
    </>
  );
};
